import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { MIX_PANEL } from '@Constants/types';
import {
    SelectableContainer,
    SelectableActionsContainer,
    SelectableTextContainer,
    SelectableText
} from './Selectable.style';
import { CustomMarker } from '@Components/CustomMarker';
import { buttons } from 'seal-ui-components';
import { CopyIcon } from '@Components/UI/Icons';
import { InsertIcon } from '@Components/UI/Icons/InsertIcon';
import { CheckIcon } from '@Components/UI/Icons';
import copy from 'copy-to-clipboard';
import insertTextAtCursorPosition from '@Actions/wordApiActions';
import { guid } from '@Utils/commonUtils';
import mixpanelEvents from '@Mixpanel';

const Selectable = ({
    className,
    originalText,
    lines = 4,
    moreText = 'Read more',
    lessText = 'Read less',
    withInsert = false,
    clampNoButtons = false,
    clausesCount,
    clauseId,
    clausePriority,
    ...rest
}) => {
    let el = useRef(null);
    let lineHeight = useRef(0);
    let start = useRef(0);
    let middle = useRef(0);
    let end = useRef(0);
    let watch = useRef(true);

    const [noClamp, setNoClamp] = useState(false);
    const [expanded, setExpanded] = useState(true);
    const [text, setText] = useState(`.`);
    const [showCopied, setShowCopied] = useState(false);

    const dispatch = useDispatch();

    if (!originalText) return null;

    const moveRefs = maxHeight => {
        if (el.current.clientHeight <= maxHeight) {
            start.current = middle.current + 1;
        } else {
            end.current = middle.current - 1;
        }
    };

    const getEllipsis = () => (watch.current && !noClamp ? '...' : '');

    const clampLines = () => {
        if (!el.current) return;
        setText(``);

        let maxHeight = lineHeight.current * lines + 1;

        start.current = 0;
        middle.current = 0;
        end.current = originalText.length;

        while (start.current <= end.current) {
            middle.current = Math.floor((start.current + end.current) / 2);
            el.current.innerText = originalText.slice(0, middle.current);
            if (middle.current === originalText.length) {
                setText(originalText);
                setNoClamp(true);
                return;
            }
            moveRefs(maxHeight);
        }
        el.current.innerText = originalText.slice(0, middle.current - 5) + getEllipsis();
        setText(originalText.slice(0, middle.current - 5) + getEllipsis());
    };

    const getRange = () => {
        if (!window.getSelection && !(document.selection && document.selection.type !== 'Control'))
            return console.error('Not supported');

        if (window.getSelection) {
            const selection = window.getSelection();
            const { baseNode } = selection;

            const selectedText = selection.toString();

            if (
                selectedText &&
                el.current.innerText.includes(selectedText) &&
                el.current.className === baseNode.parentNode.className
            ) {
                return selectedText;
            }
        }

        if (document.selection && document.selection.type !== 'Control')
            return document.selection.createRange().text;
    };

    const clickHandler = () => {
        watch.current = !watch.current;
        watch.current ? clampLines() : setText(originalText);
        setExpanded(!expanded);

        let insertClauseLanguagePriority = 1; // default to primary language level
        if (clausePriority) insertClauseLanguagePriority = clausePriority;

        const mixpanel = {
            clausesInGroupCount: clausesCount || 0,
            clauseClickedId: clauseId,
            suggestedClauseInserted: insertClauseLanguagePriority
        };
        dispatch({ type: MIX_PANEL.MIXPANEL_DATA_CLAUSE_LIBRARY, payload: mixpanel });
        withInsert
            ? mixpanelEvents.detailedClauseToggleShowFullText()
            : mixpanelEvents.detailedClauseToggleShowFullDescription();
    };

    const copyText = () => {
        const selectedRangeText = getRange() || originalText;
        copy(selectedRangeText, {
            format: 'text/plain',
            onCopy: () => {
                setShowCopied(true);
                setTimeout(() => {
                    setShowCopied(false);
                }, 2000);
            }
        });
    };

    const insertText = () => {
        const selectedRangeText = getRange() || originalText;
        insertTextAtCursorPosition(selectedRangeText);

        let insertClauseLanguagePriority = 1; // default to primary language level
        if (clausePriority) insertClauseLanguagePriority = clausePriority;

        const mixpanelClause = {
            clausesInGroupCount: clausesCount || 0,
            clauseClickedId: clauseId,
            suggestedClauseInserted: insertClauseLanguagePriority
        };
        dispatch({ type: MIX_PANEL.MIXPANEL_DATA_CLAUSE_LIBRARY, payload: mixpanelClause });

        const mixpanelScorecard = {
            suggestedClauseInserted: insertClauseLanguagePriority
        };
        dispatch({ type: MIX_PANEL.MIXPANEL_DATA_SCORECARD, payload: mixpanelScorecard });

        const tabName = location.pathname.split('/')[1];
        if (tabName === 'scorecards') {
            mixpanelEvents.insertlinkedclause();
        }

        if (tabName === 'clauses') {
            mixpanelEvents.detailedClauseViewInsertClause();
        }
    };

    const getButton = () => {
        if (noClamp || clampNoButtons) return;

        let buttonText = watch.current ? moreText : lessText;

        return (
            <buttons.Button
                size="medium"
                appearance="tertiary"
                className="clamp__more"
                type="button"
                aria-expanded={expanded}
                onClick={clickHandler}
            >
                {buttonText}
            </buttons.Button>
        );
    };

    const getActions = () => {
        const insertIcon = withInsert && (
            <InsertIcon style={{ width: '1.2rem', height: '1.2rem' }} />
        );
        let copyIcon = showCopied ? (
            <CheckIcon style={{ width: '1.2rem', height: '1.2rem' }} />
        ) : (
            <CopyIcon style={{ width: '1.2rem', height: '1.2rem' }} />
        );

        return (
            <SelectableActionsContainer className="selectable__actions">
                {withInsert && (
                    <buttons.Button
                        className="selectable__insert"
                        size="medium"
                        appearance="tertiary"
                        onClick={insertText}
                        icon={insertIcon}
                        title="Click to Insert"
                    />
                )}
                <buttons.Button
                    className="selectable__copy"
                    size="medium"
                    appearance="tertiary"
                    onClick={copyText}
                    icon={copyIcon}
                    title="Click to Copy"
                />
            </SelectableActionsContainer>
        );
    };

    useEffect(() => {
        if (originalText) {
            lineHeight.current = el.current.clientHeight - 8;
            clampLines();
        }
    }, []);

    return (
        <SelectableContainer className={className} key={guid()} {...rest}>
            <SelectableTextContainer tabIndex="0">
                {getActions()}
                <CustomMarker>
                    <SelectableText ref={el} aria-hidden={!expanded} className="selectable__text">
                        {text}
                    </SelectableText>
                </CustomMarker>
            </SelectableTextContainer>
            {getButton()}
        </SelectableContainer>
    );
};

export default Selectable;
